import React, {useEffect, useMemo} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import useTranslate from '../../../core/languages/hooks/useTranslate';
import useCurrentLanguage from '../../../core/languages/hooks/useCurrentLanguage';
import useTransLocationsGetCollectByTransportAndCityId from '../../hooks/useTransLocationsGetCollectByTransportAndCityId';
import TransportLocation from '../../types/TransportLocation';
import { getTransportLocationLabel } from '../../utils/transportLabels';
import LocationDetailsInput from './LocationDetailsInput';
import {TransportEnum} from "../../enums/TransferTransportEnum";
import HotelDetail from "../../../hotels/types/HotelDetail";

interface DepartureLocationFromProps {
  packageWizard?: boolean;
  hotelInfo?: HotelDetail;
}
const DepartureLocationFrom:  React.FC<DepartureLocationFromProps> = ({packageWizard = false, hotelInfo}) => {
    const t = useTranslate();
    const languageCode = useCurrentLanguage();
    const { control, formState: { errors }, getValues, watch, setValue } = useFormContext();

    const transportRaw = TransportEnum.OTHER;
    const transport = transportRaw ? Number(transportRaw) : null;
    const city = getValues('city');
    const selectedLocationId = watch('departureLocationFrom');
    const hasTransport = transport !== undefined && transport !== null;
    const shouldExecute = hasTransport && !!city && city !== '';
    
    const { data: transportLocations = [], isLoading } = useTransLocationsGetCollectByTransportAndCityId(
        transport,
        city,
        { execute: shouldExecute }
    );

    // Filter only locations that have requiredMoreDetails true or hotel not null - only if OTHER transport
    // Order: first locations with hotel, then locations with requiredMoreDetails
    let filteredTransportLocations = transportLocations.filter(
                (location: TransportLocation) => location.requiredMoreDetails || (location as any).hotel
            )
            .sort((a, b) => {
                // Primero las que tienen hotel
                const aHasHotel = !!(a as any).hotel;
                const bHasHotel = !!(b as any).hotel;
                if (aHasHotel !== bHasHotel) {
                    return aHasHotel ? -1 : 1;
                }
                // Si ambas tienen o no hotel, las con requiredMoreDetails van primero
                const aRequired = a.requiredMoreDetails;
                const bRequired = b.requiredMoreDetails;
                if (aRequired !== bRequired) {
                    return aRequired ? -1 : 1;
                }
                return 0;
            })

  // Create a stable string key from filtered locations IDs to detect content changes
  const filteredLocationsKey = useMemo(
    () => filteredTransportLocations.map(l => l.id).join(','),
    [filteredTransportLocations]
  );

  // Auto-select the hotel location for packageWizard
  useEffect(() => {
    if (packageWizard && hotelInfo?.hotelObject.id && filteredTransportLocations.length > 0 && !selectedLocationId) {
      const hotelId = hotelInfo.hotelObject.id;
      const hotelLocation = filteredTransportLocations.find(loc => {
        const locHotelId = (loc as any).hotel ? parseInt((loc as any).hotel.split('/').pop()) : null;
        return locHotelId === hotelId;
      });

      if (hotelLocation) {
        setValue('departureLocationFrom', hotelLocation.id);
      }
    }
  }, [packageWizard, hotelInfo?.hotelObject.id, filteredLocationsKey, selectedLocationId, setValue]);

    // Find the selected location to know if it requires more details
    const selectedLocation = filteredTransportLocations.find(loc => String(loc.id) === String(selectedLocationId));
    const requiresDetails = selectedLocation?.requiredMoreDetails || false;

    return (
        <div>
            <Controller
                name="departureLocationFrom"
                control={control}
                rules={{ required: hasTransport ? t('common.feedback.field_required') : false }}
                render={({ field }) => (
                    <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                            {getTransportLocationLabel(t, transport, 'departureFrom')}
                        </label>
                        <select
                            {...field}
                            disabled={!hasTransport || isLoading || packageWizard}
                            className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900
                            text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500
                            disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <option value="">
                                {!hasTransport ? t('transfers.message.selectTransportFirst') : (isLoading ? t('common.actions.loading') : t('common.actions.select'))}
                            </option>
                            {filteredTransportLocations.map((location: TransportLocation) => (
                                <option key={location.id} value={location.id}>
                                    {location.trName[languageCode] || location.trName.en}
                                </option>
                            ))}
                        </select>
                        {errors.departureLocationFrom && (
                            <p className="mt-1 text-sm text-red-600">
                                {errors.departureLocationFrom.message}
                            </p>
                        )}
                    </div>
                )}
            />
            {requiresDetails && (
                <LocationDetailsInput
                    locationId={selectedLocationId}
                    fieldName="departureLocationFromDetails"
                    label={t('transfers.fields.departureLocationFromDetails')}
                />
            )}
        </div>
    );
};

export default DepartureLocationFrom;
